import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import SmartLoader from "../../components/loader/SmartLoader";
import {
  SmartMessage,
  SmartHeader,
  SmartApplicants,
} from "../../components/shared";
import Documents from "../../components/documents/Documents";
import GridView from "../../components/grid/GridView";
import RmbalConfirmModal from "../../components/rmbal-confirm-modal/Modal";
import {
  getApplication,
  getPreConditions,
  setTouched,
} from "../app/actions";
import NewCollateral from "./components/NewCollateral";
import CurrentCollateral from "./components/CurrentCollateral";
import Cost from "./components/Cost";
import { appStatus, appType } from "../../utils/constants";
import { getConsolidationInfo, getConsolidationRmBal, saveConsolidation, resetConsolidationState, setLoanType } from "./actions";
import LoanTypeDropdown from "./components/LoanTypeDropdown";

const hasRequiredCarConsolidation = (data) => {
  if (!data) return false;

  const { newCollateral, currentCollateral, applicants, loanNetAmount } = data;

  // Check if required fields exist and have valid values
  const hasNewCollateral = newCollateral?.salesAmt > 0 && newCollateral?.collateralId;
  const hasCurrentCollateral = currentCollateral?.salesAmt > 0 && currentCollateral?.collateralId;
  const hasValidLoanAmount = loanNetAmount > 0;

  return hasNewCollateral && hasCurrentCollateral && hasValidLoanAmount;
};

class CarConsolidationLoan extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showRmbalModal: false
    };
  }

  componentDidMount() {
    const { consolidation, location } = this.props;
    
    if (location.state) {
      this.loadApplicationHandler(location.state.year, location.state.serialNumber);
    } else if (consolidation.year !== '' && consolidation.year !== '0000' && consolidation.serialNumber !== '' && consolidation.serialNumber !== '000000') {
      this.loadApplicationHandler(consolidation.year, consolidation.serialNumber);
    }
  }

  loadApplicationHandler = (year, serialNumber) => {
    this.props.dispatch(getConsolidationInfo({ year, serialNumber }));
  };

  componentDidUpdate(prevProps) {
    const { consolidation, dispatch } = this.props;
    
    // Check if relevant fields changed to set touched
    const prevNewCollateral = prevProps.consolidation.newCollateral;
    const prevCurrentCollateral = prevProps.consolidation.currentCollateral;
    const prevLoanNetAmount = prevProps.consolidation.loanNetAmount;
    const prevApplicant = prevProps.consolidation.applicant;
    
    const currentNewCollateral = consolidation.newCollateral;
    const currentCurrentCollateral = consolidation.currentCollateral;
    const currentLoanNetAmount = consolidation.loanNetAmount;
    const currentApplicant = consolidation.applicant;
    
    const relevantFieldsChanged = 
      (currentNewCollateral?.salesAmt > 0 && prevNewCollateral?.salesAmt !== currentNewCollateral?.salesAmt) || 
      (currentNewCollateral?.collateralId && prevNewCollateral?.collateralId !== currentNewCollateral?.collateralId) || 
      (currentCurrentCollateral?.salesAmt > 0 && prevCurrentCollateral?.salesAmt !== currentCurrentCollateral?.salesAmt) || 
      (currentCurrentCollateral?.collateralId && prevCurrentCollateral?.collateralId !== currentCurrentCollateral?.collateralId) || 
      (currentLoanNetAmount > 0 && prevLoanNetAmount !== currentLoanNetAmount) ||
      (currentApplicant && currentApplicant.ssn && prevApplicant?.ssn !== currentApplicant?.ssn);
    
    if (relevantFieldsChanged) {
      dispatch(setTouched(true));
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(resetConsolidationState());
  }

  handleApplication = (status) => {
    const { consolidation, dispatch } = this.props;

    const data = {
      status,
      applicants: [consolidation.applicant].filter(a => a?.ssn),
      newCollateral: consolidation.newCollateral,
      currentCollateral: consolidation.currentCollateral,
      loanNetAmount: consolidation.loanNetAmount
    };

    if (!hasRequiredCarConsolidation(data)) {
      return;
    }
    dispatch(saveConsolidation(status));
  };

  handleRmbalConfirm = () => {
    const { dispatch } = this.props;
    this.setState({ showRmbalModal: false });
    
    dispatch(getConsolidationRmBal());
  };

  handleRmbalCancel = () => {
    this.setState({ showRmbalModal: false });
  };

  handleLoanTypeSelect = (loanType) => {
    const { dispatch } = this.props;
    dispatch(setLoanType(loanType));
  };

  toggleRmbalModal = (show) => {
    this.setState({ showRmbalModal: show });
  };

  render() {
    const { consolidation } = this.props;
    const { showRmbalModal } = this.state;
    const { editable, statusText, message1, message2 } = consolidation;

    // add a loader when all consolidation requests are being fetched
    let isFetching = consolidation.isFetchingConsolidationInfo ||
                     consolidation.isFetchingSaveConsolidation ||
                     consolidation.isFetchingCalculateConsolidation ||
                     consolidation.isFetchingConsolidationRmbal ||
                     consolidation.isFetchingCarInfo;

    return (

      <>
        <SmartLoader loading={isFetching} />
        <SmartHeader
          title="sitemap.CarConsolidationLoan"
          subtitle={statusText || "sitemap.Application"}
          message1={message1}
          message2={message2}
        >
          <Documents />
        </SmartHeader>
        <LoanTypeDropdown
          handleSelect={this.handleLoanTypeSelect}
          selectedType={consolidation.productType}
          error={""}
          editable={editable}
        />
        <GridView
          grid1={<NewCollateral title="Consolidation.NewCollateral" />}
          grid2={
            <CurrentCollateral
              title="Consolidation.CurrentCollateral"
              toggleRmbalModal={this.toggleRmbalModal}
            />
          }
          grid3={(
            <SmartApplicants
              restrictApplicant="person"
              title="Application.PersonsHeader"
              showSecondApplicant={false}
              applicationType={10}
            />
          )}
          calculator={<Cost />}
          message={<SmartMessage disabled={!editable} />}
          displayButtons={editable}
          editable={editable}
          handleSaveApplication={() => this.handleApplication(appStatus.save)}
          handleSendApplication={() => this.handleApplication(appStatus.send)}
        />

        <RmbalConfirmModal
          showModal={showRmbalModal}
          handleConfirm={this.handleRmbalConfirm}
          handleCancel={this.handleRmbalCancel}
          currentCollateral={consolidation.currentCollateral}
        />
      </>
    );
  }
}

CarConsolidationLoan.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      year: PropTypes.number,
      serialNumber: PropTypes.string,
    }),
  }).isRequired,
  consolidation: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  consolidation: state.consolidation,
});

export default connect(mapStateToProps)(CarConsolidationLoan);
